import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useChatStore } from '@/store';

// The sidebar/drawer layouts only need chat *metadata* (title, flags,
// timestamps, ownership) — never `messages`. Subscribing to `state.chats`
// directly re-renders the entire app shell on every streaming token because
// the active chat object (and the chats array) get new references on each
// flush. This hook projects each chat down to a messages-free meta object and
// keeps the previous object reference whenever no metadata actually changed,
// so `useShallow` sees a stable array during streaming and the sidebar only
// re-renders on real changes (new chat, rename, pin, delete, etc.).

function shallowEqualObjects(a, b) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function createSidebarChatSelector() {
  const metaByRef = new WeakMap();
  let metaById = new Map();
  let sourceChats, result;
  return state => {
    if (state.chats === sourceChats) return result;
    const nextById = new Map();
    result = state.chats.map(chat => {
      let meta = metaByRef.get(chat);
      if (!meta) {
        const { messages, ...next } = chat;
        const prev = metaById.get(chat.id);
        meta = prev && shallowEqualObjects(prev, next) ? prev : next;
        metaByRef.set(chat, meta);
      }
      nextById.set(chat.id, meta);
      return meta;
    });
    // Retain only this sidebar's current chats, not every account/chat ever
    // opened. Unmounting also releases the selector and its metadata cache.
    metaById = nextById;
    sourceChats = state.chats;
    return result;
  };
}

/**
 * Metadata-only view of the chat list for sidebars/drawers. Same shape as
 * `state.chats` minus `messages`, with stable references across streaming
 * updates.
 */
export function useSidebarChats() {
  const selector = useMemo(createSidebarChatSelector, []);
  return useChatStore(useShallow(selector));
}

export default useSidebarChats;
